/**
 * Pure functions behind the shared `PermissionTree` (owned by CT-5612, reused by the role
 * builder CT-5613). All parent/child logic lives here, never in JSX.
 *
 * Model
 * - A TREE is the studio catalog: `[{ id, label, description, levels?, children? }]`.
 * - A DRAFT is a flat map `{ [nodeId]: { on, level, origin, via, manualLevel } }` with an entry for
 *   EVERY node: `on` the row's switch, `level` its access level (`own|groups|tenant`, narrowest
 *   first; each one includes the previous), `origin` where the row comes from (`none` | `role`
 *   inherited, still off until pinned | `direct` set here), `via` the names of the roles it is
 *   inherited from, and `manualLevel` the level chosen on this row (`null` means it follows its
 *   parent). A role grant counts as chosen: changing the parent does not move it.
 * - GRANTS are the API rows `{ permission_id, level, source?: 'role'|'direct', via_roles? }`.
 *   A grant without `source` is a direct one (the role builder only has direct rows).
 *
 * Level rules (Thomas, CT-5563): a row starts at the narrowest level it accepts (Own, unless
 * `node.levels` lists fewer). Turning a parent on puts its children at that same level. Moving a
 * parent moves the children that follow it; a child adjusted by hand keeps its level, clamped so
 * it is never broader than the parent. Which permissions offer only Tenant is still for
 * engineering to confirm — pass them as `node.levels` when that list exists. A catalog
 * `default_level` is not a starting level.
 *
 * "Never broader than the parent" (CT-5847), and the choices behind it:
 * - Only a parent that is ON sets a ceiling (`ceilingOf`). An off parent has no level of its own,
 *   so its children are not limited by it. This holds before and after a reload (`buildDraft` shows
 *   an off parent at the widest level of its rows on, which then is only a display value).
 * - Turning a parent on keeps rows that are already on at their level, clamped to the parent's
 *   new level; only rows turned on now take the parent's level (nearest below when not offered).
 * - A row inherited from a role keeps its role level while locked; once turned on (pinned) it is
 *   clamped to its parent. Grandchildren of a locked row are capped by that row's level.
 * - A row whose narrowest level is broader than its parent (a Tenant-only child under an Own
 *   parent) cannot be turned on (`isBlockedByCeiling`); a parent cannot be moved narrower than the
 *   narrowest level of its rows that are on.
 * - `buildDraft` does NOT rewrite saved rows broader than their parent: saving never changes data
 *   the user did not touch. The row keeps its level, shown as too broad, and the user narrows it.
 *
 * Every function returns a new draft; none mutates its input.
 */
import { ACCESS_LEVELS, DEFAULT_ACCESS_LEVEL } from '../constants/permissions.js';

export const ORIGIN = Object.freeze({ NONE: 'none', ROLE: 'role', DIRECT: 'direct' });

export const SOURCE_TAG = Object.freeze({ PINNED: 'pinned', MODIFIED: 'modified' });

/** Rank of a level, derived from `ACCESS_LEVELS` (narrowest first) so the two cannot drift. */
const LEVEL_RANK = Object.freeze(Object.fromEntries(ACCESS_LEVELS.map((level, index) => [level, index])));

/** Levels this node offers, narrowest first. All three until `node.levels` says otherwise. */
export const levelsOf = (node) => {
  const listed = (node?.levels ?? []).filter((level) => LEVEL_RANK[level] != null);
  if (!listed.length) return ACCESS_LEVELS;
  return ACCESS_LEVELS.filter((level) => listed.includes(level));
};

const narrowestLevel = (node) => levelsOf(node)[0] ?? DEFAULT_ACCESS_LEVEL;

/** True when `level` covers more records than `ceiling` (Own < Groups < Tenant). */
export const isBroaderLevel = (level, ceiling) =>
  ceiling != null && (LEVEL_RANK[level] ?? 0) > (LEVEL_RANK[ceiling] ?? 0);

const clampLevel = (level, ceiling) => (isBroaderLevel(level, ceiling) ? ceiling : level);

/** Ceiling a parent's entry sets on its children: its level while it is on, none while off. */
export const ceilingOf = (parentEntry) => (parentEntry?.on ? parentEntry.level : null);

/** True when even the narrowest level `node` offers is broader than `ceiling` (it cannot be on). */
export const isBlockedByCeiling = (node, ceiling) => ceiling != null && isBroaderLevel(narrowestLevel(node), ceiling);

/** Nearest offered level that is not broader than `desired`. */
const fitLevel = (node, desired) => {
  const allowed = levelsOf(node);
  if (allowed.includes(desired)) return desired;
  const rank = LEVEL_RANK[desired] ?? 0;
  return [...allowed].reverse().find((level) => (LEVEL_RANK[level] ?? 0) <= rank) ?? allowed[0];
};

const findNode = (nodes, id) => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children ?? [], id);
    if (found) return found;
  }
  return null;
};

/** Parent of `id`, `null` when `id` is a root, `undefined` when `id` is missing. */
const findParent = (nodes, id, parent = null) => {
  for (const node of nodes) {
    if (node.id === id) return parent;
    if (node.children?.length) {
      const found = findParent(node.children, id, node);
      if (found !== undefined) return found;
    }
  }
  return undefined;
};

/** Every descendant of `node`, depth first, without the node itself. */
export const getDescendants = (node) =>
  (node.children ?? []).flatMap((child) => [child, ...getDescendants(child)]);

const allNodes = (nodes) => nodes.flatMap((node) => [node, ...getDescendants(node)]);

/** Build the draft of a tree from the API grants (see the model above). */
export const buildDraft = (nodes, grants = []) => {
  const byId = new Map(grants.map((g) => [g.permission_id, g]));
  const draft = {};
  allNodes(nodes).forEach((node) => {
    const grant = byId.get(node.id);
    if (!grant) {
      draft[node.id] = { on: false, level: narrowestLevel(node), origin: ORIGIN.NONE, via: [], manualLevel: null };
      return;
    }
    const via = (grant.via_roles ?? []).map((r) => r.name);
    if (grant.source === 'role') {
      draft[node.id] = { on: false, level: grant.level, origin: ORIGIN.ROLE, via, manualLevel: grant.level };
      return;
    }
    draft[node.id] = { on: true, level: grant.level, origin: ORIGIN.DIRECT, via, manualLevel: null };
  });
  // A parent that is off has no level of its own; it shows the widest level of the rows on below it,
  // so saved data never reads as "broader than its parent".
  const widenOffParents = (list) => {
    list.forEach((node) => {
      widenOffParents(node.children ?? []);
      const entry = draft[node.id];
      if (!node.children?.length || entry.on || entry.origin === ORIGIN.ROLE) return;
      const widest = getDescendants(node)
        .map((descendant) => draft[descendant.id])
        .filter((child) => child.on)
        .reduce((acc, child) => (isBroaderLevel(child.level, acc) ? child.level : acc), entry.level);
      draft[node.id] = { ...entry, level: widest };
    });
  };
  widenOffParents(nodes);
  // A direct row whose level differs from its parent was adjusted by hand. A row broader than its
  // parent is left as saved (see the header): it shows as too broad until the user narrows it.
  const markManual = (list, parentLevel) => {
    list.forEach((node) => {
      const entry = draft[node.id];
      if (entry.origin !== ORIGIN.ROLE && entry.on && parentLevel != null && entry.level !== parentLevel) {
        draft[node.id] = { ...entry, manualLevel: entry.level };
      }
      markManual(node.children ?? [], entry.level);
    });
  };
  markManual(nodes, null);
  return draft;
};

/** A row counts as on when its switch is on or, with `effective`, when a role grants it. */
const isOn = (entry, effective) => Boolean(entry?.on) || (effective && entry?.origin === ORIGIN.ROLE);

/**
 * Switch state of a node: `{ checked, partial, on, total }`. A leaf is checked when on. A parent
 * is checked while ITS OWN switch is on: turning a child off never turns the parent off (CT-5841),
 * so the switches on screen are the permissions really granted. It is `partial` ("Partial · on of
 * total") when some, not all, of its descendants are on. `effective` also counts rows inherited
 * from a role (read view, counter).
 */
export const getNodeState = (node, draft, { effective = false } = {}) => {
  const descendants = getDescendants(node);
  const on = descendants.filter((d) => isOn(draft[d.id], effective)).length;
  const selfOn = isOn(draft[node.id], effective);
  if (!descendants.length) return { checked: selfOn, partial: false, on: 0, total: 0 };
  // An off parent with any row on below reads Partial; an on parent reads Partial while some rows below are off.
  const partial = selfOn ? on < descendants.length : on > 0;
  return { checked: selfOn, partial, on, total: descendants.length };
};

/**
 * Turn a node on or off. A parent applies to itself and its whole subtree. Turning on puts the
 * parent at the narrowest level it accepts; rows below that are already on keep their level
 * (clamped to the parent's new level) and only rows turned on now take the parent's level. A leaf
 * keeps the level already chosen, clamped to its parent. An inherited row turned on keeps its role
 * level, clamped to its parent, and its origin (the diff then tags it Pinned); turned off again, a
 * row that roles still grant goes back to inherited. A row that cannot be on under a narrower
 * parent (`isBlockedByCeiling`) is left as it is.
 */
export const toggleNode = (nodes, draft, id, on) => {
  const node = findNode(nodes, id);
  if (!node) return draft;
  const parent = findParent(nodes, id);
  const next = { ...draft };

  const apply = (n, ceiling) => {
    const entry = draft[n.id];
    if (on && !entry.on && isBlockedByCeiling(n, ceiling)) return;
    const isRoot = n.id === id;
    // Turning off a row a role still grants (a saved pin) makes it inherited again, not removed.
    const origin = !on && entry.via?.length ? ORIGIN.ROLE : entry.origin;
    let level = entry.level;
    let manualLevel = entry.manualLevel ?? null;
    if (on) {
      const keeps = entry.on || entry.origin === ORIGIN.ROLE || (isRoot && !n.children?.length);
      if (keeps) {
        level = fitLevel(n, clampLevel(entry.level, ceiling));
        manualLevel = manualLevel != null && level !== entry.level ? level : manualLevel;
      } else {
        level = fitLevel(n, clampLevel(isRoot ? narrowestLevel(n) : ceiling, ceiling));
        manualLevel = null;
      }
    }
    next[n.id] = { ...entry, on, level, origin, manualLevel };
    (n.children ?? []).forEach((child) => apply(child, on ? level : ceiling));
  };
  apply(node, parent ? ceilingOf(draft[parent.id]) : null);
  return next;
};

/** An inherited row that is still off cannot change level until it is turned on (pinned). */
export const isLevelLocked = (entry) => entry.origin === ORIGIN.ROLE && !entry.on;

/** Widest of the narrowest levels of the rows on below `node`: a parent cannot go narrower. */
const requiredLevel = (node, draft) =>
  getDescendants(node)
    .filter((d) => draft[d.id].on)
    .reduce((acc, d) => (acc == null || isBroaderLevel(narrowestLevel(d), acc) ? narrowestLevel(d) : acc), null);

/**
 * Set the access level of a node. Children that follow it take the same level; a child with
 * `manualLevel` keeps that choice. Nothing ends up broader than its parent (the ceiling of an off
 * parent does not count), a parent does not go narrower than a row on below it requires (a
 * Tenant-only child), and a locked inherited row is left as the role granted it. Choosing the
 * level the row already has changes nothing, and choosing its parent's level makes it follow the
 * parent again instead of pinning it.
 */
export const setLevel = (nodes, draft, id, level) => {
  const node = findNode(nodes, id);
  if (!node || isLevelLocked(draft[id])) return draft;
  const next = { ...draft };
  const parent = findParent(nodes, id);
  const ceiling = parent ? ceilingOf(next[parent.id]) : null;
  let applied = fitLevel(node, ceiling != null ? clampLevel(level, ceiling) : level);
  const required = requiredLevel(node, draft);
  if (required != null && isBroaderLevel(required, applied)) applied = fitLevel(node, required);
  if (applied === draft[id].level) return draft;
  const follows = !parent || applied === next[parent.id].level;
  next[id] = { ...draft[id], level: applied, manualLevel: follows ? null : applied };

  const cascade = (current, cap) => {
    (current.children ?? []).forEach((child) => {
      const entry = next[child.id];
      if (isLevelLocked(entry)) {
        cascade(child, clampLevel(entry.level, cap));
        return;
      }
      const desired = entry.manualLevel != null ? clampLevel(entry.manualLevel, cap) : cap;
      const childLevel = fitLevel(child, desired);
      next[child.id] = { ...entry, level: childLevel };
      cascade(child, childLevel);
    });
  };
  cascade(node, applied);
  return next;
};

/**
 * What changed against the saved draft: `tags` (`pinned` for an inherited row turned on,
 * `modified` for a new permission or a changed level) and `isDirty`. Removing a direct row is a
 * change without tag. The level of a row that is off is ignored.
 */
export const diffDraft = (base, draft) => {
  const tags = {};
  let isDirty = false;
  Object.keys(draft).forEach((id) => {
    const before = base[id];
    const after = draft[id];
    if (after.on && !before.on) {
      tags[id] = before.origin === ORIGIN.ROLE ? SOURCE_TAG.PINNED : SOURCE_TAG.MODIFIED;
      isDirty = true;
    } else if (after.on && before.on && after.level !== before.level) {
      tags[id] = SOURCE_TAG.MODIFIED;
      isDirty = true;
    } else if (!after.on && before.on) {
      isDirty = true;
    }
  });
  return { tags, isDirty };
};

/**
 * Role names shown as "via <Role>" on a row. A leaf shows its own roles while it is still
 * inherited (or, in edit mode, while it is inherited and off). With `aggregate`, a parent shows the
 * roles of any descendant that is still inherited, so it reads as role-derived while some of its
 * subtree is (the prototype does it below the top-level modules, which only carry the Partial badge).
 */
export const getViaRoles = (node, draft, { isEdit = false, aggregate = false } = {}) => {
  const isAggregate = aggregate && Boolean(node.children?.length);
  const entries = isAggregate ? getDescendants(node).map((d) => draft[d.id]) : [draft[node.id]];
  const roles = entries
    .filter((entry) => entry?.origin === ORIGIN.ROLE && (isAggregate || !isEdit || !entry.on))
    .flatMap((entry) => entry.via);
  return [...new Set(roles)];
};

/** Edit-time tag of a row: with `aggregate`, a parent is `pinned` when any row below it is (pinned wins over modified). */
export const getRowTag = (node, tags, { aggregate = false } = {}) => {
  if (tags[node.id] === SOURCE_TAG.PINNED) return SOURCE_TAG.PINNED;
  const pinnedBelow = aggregate && getDescendants(node).some((d) => tags[d.id] === SOURCE_TAG.PINNED);
  return pinnedBelow ? SOURCE_TAG.PINNED : tags[node.id];
};

/** The rows that are on, as the `{ permission_id, level }` payload of a save. */
export const toGrants = (draft) =>
  Object.entries(draft)
    .filter(([, entry]) => entry.on)
    .map(([permission_id, entry]) => ({ permission_id, level: entry.level }));

/** `{ direct, fromRoles }`: rows set here vs rows still only inherited from a role. */
export const summarizeDraft = (draft) =>
  Object.values(draft).reduce(
    (acc, entry) => ({
      direct: acc.direct + (entry.on ? 1 : 0),
      fromRoles: acc.fromRoles + (!entry.on && entry.origin === ORIGIN.ROLE ? 1 : 0),
    }),
    { direct: 0, fromRoles: 0 },
  );
